import { Component, inject, Input, Output, EventEmitter, OnChanges, SimpleChanges, HostListener } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { NgClass } from '@angular/common';
import { filterByFields } from '@/app/utils/filter-utils';
import { Client } from '@/app/features/cliente/models/cliente.interface';
import { ClientStore } from '@/app/core/states/clientes-state/client-state.store';

export interface ActionIcon {
  label: string;
  icon: string;
  action: (item: any) => void;
}

export type ColumnDef = {
  key: string;
  displayName: string;
  isLink?: boolean;
  routeBase?: string;
  type?: 'text' | 'date';
  formatter?: (value: any, row: any) => string;
};

export type TableColumn = ColumnDef;

@Component({
  selector: 'app-table-component',
  standalone: true,
  imports: [FormsModule, RouterLink, NgClass],
  templateUrl: './table-component.html',
  styleUrl: './table-component.scss',
})
export class TableComponent implements OnChanges {
  private router = inject(Router);
  readonly store = inject(ClientStore);

  @Input() columns: ColumnDef[] = [];

  @Input() data: any[] = [];
  @Input() labelForSearch: string = '';
  @Input() nameButton: string = 'Nuevo';

  @Input() editMode: boolean = false;
  @Input() actionsIcons: ActionIcon[] = [];

  @Input() menu: boolean = false;
  @Input() options: { label: string; route?: string; action?: (item: any) => void }[] = [];

  @Input() onAdd: () => void = () => {};
  @Input() onEdit!: (item: any) => void;
  @Input() onDelete!: (item: any) => void;

  @Input() isServerPaginated: boolean = false;
  @Input() loading: boolean = false;

  protected get isBusy(): boolean {
    return this.awaitingSearch || this.loading;
  }
  @Input() totalItemsOverride: number | null = null;
  @Input() currentPageInput: number = 1;

  @Output() itemsPerPageChange = new EventEmitter<number>();
  @Output() pageChange = new EventEmitter<number>();
  @Output() searchChange = new EventEmitter<string>();

  currentMenuItem: any = null;
  menuPosition: 'down' | 'up' = 'down';

  toggleMenu(item: any, event?: Event) {
    if (event) {
      const button = event.target as HTMLElement;
      const rect = button.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const menuHeight = 150; // Approximate height of the dropdown menu
      if (spaceBelow < menuHeight && spaceAbove > spaceBelow) {
        this.menuPosition = 'up';
      } else {
        this.menuPosition = 'down';
      }
    }
    this.currentMenuItem = this.currentMenuItem === item ? null : item;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (this.currentMenuItem) {
      this.currentMenuItem = null;
    }
  }

  onOptionClick(option: { label: string; route?: string; action?: (item: any) => void }, item: Client): void {
    if (option.action) option.action(item);
    else if (option.route) this.router.navigateByUrl(option.route);
  }

  // Filtro
  filterData: any[] = [];
  searchTerm: string = '';
  private static readonly SEARCH_DEBOUNCE_MS = 1000;
  private searchDebounce?: ReturnType<typeof setTimeout>;
  protected awaitingSearch: boolean = false;
  filters: string[] = [];
  columnsExcludes = ['activo', 'acciones'];

  // Paginación
  @Input() itemsPerPage: number = 10;
  currentPage: number = 1;
  totalItems: number = 0;
  totalPages: number = 0;
  paginatedData: any[] = [];

  ngOnChanges(changes: SimpleChanges) {
    if (changes['columns']) {
      this.filters = this.columns
        .filter((column) => !this.columnsExcludes.includes(column.key))
        .map((column) => column.key);
      if (!this.isServerPaginated) {
        this.applyFilter();
      }
    }

    if (changes['data']) {
      if (this.isServerPaginated) {
        this.filterData = this.data;
        this.awaitingSearch = false;
      } else {
        this.applyFilter();
      }
      this.updatePagination();
    }

    if (changes['itemsPerPage']) {
      this.currentPage = 1;
      this.updatePagination();
    }

    if (changes['currentPageInput']) {
      this.currentPage = this.currentPageInput;
      this.updatePagination();
    }

    if (changes['totalItemsOverride']) {
      this.updatePagination();
    }
  }

  editar(item: any) {
    if (this.onEdit) this.onEdit(item);
  }

  updatePagination() {
    if (this.isServerPaginated) {
      this.totalItems = this.totalItemsOverride ?? this.filterData.length;
      this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage);
      this.paginatedData = this.filterData;
    } else {
      this.totalItems = this.filterData.length;
      this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage);
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      this.paginatedData = this.filterData.slice(start, end);
    }
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.pageChange.emit(this.currentPage);
      this.updatePagination();
    }
  }
  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.pageChange.emit(this.currentPage);
      this.updatePagination();
    }
  }
  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.pageChange.emit(this.currentPage);
      this.updatePagination();
    }
  }
  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, this.currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(this.totalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    for (let i = startPage; i <= endPage; i++) pages.push(i);
    return pages;
  }
  getEndItemIndex(): number {
    return Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
  }
  onItemsPerPageChange(): void {
    this.itemsPerPage = Number(this.itemsPerPage);
    this.currentPage = 1;
    this.itemsPerPageChange.emit(this.itemsPerPage);
    this.updatePagination();
  }

  // Selección
  selectedItems: Set<any> = new Set<any>();
  allSelected: boolean = false;

  toggleSelectAll() {
    if (this.allSelected) {
      this.data.forEach((item) => this.selectedItems.delete(item));
      this.allSelected = false;
    } else {
      this.data.forEach((item) => this.selectedItems.add(item));
      this.allSelected = true;
    }
  }
  toggleSelectItem(item: any) {
    if (this.selectedItems.has(item)) this.selectedItems.delete(item);
    else this.selectedItems.add(item);
    this.updateSelectionState();
  }
  updateSelectionState() {
    const totalVisible = this.data.length;
    const selectedCount = this.data.filter((item) => this.selectedItems.has(item)).length;
    this.allSelected = selectedCount > 0 && selectedCount === totalVisible;
  }
  isSelected(item: any): boolean {
    return this.selectedItems.has(item);
  }

  applyFilter(): void {
    if (this.isServerPaginated) {
      this.currentPage = 1;
      this.awaitingSearch = true;
      this.emitDebouncedSearch();
      return;
    }

    this.filterData = filterByFields(this.data, this.searchTerm, this.filters);
    this.currentPage = 1;
    this.updatePagination();
  }

  private emitDebouncedSearch(): void {
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
    }
    this.searchDebounce = setTimeout(
      () => this.searchChange.emit(this.searchTerm),
      TableComponent.SEARCH_DEBOUNCE_MS,
    );
  }

  trackByIds(index: number, item: any): string {
    if (item.id) return `id_${item.id}`;
    if (item.publicId) return `public_${item.publicId}`;
    return `index_${index}`;
  }

  //----Formato de la fecha en la tabla de campañas-------//
  displayValue(row: any, col: ColumnDef): string {
    const value = row?.[col.key];

    if (typeof col.formatter === 'function') {
      try {
        return col.formatter(value, row);
      } catch {
        // fallback
      }
    }

    if (col.type === 'date') {
      return this.formatISODateToDMY(value);
    }

    return (value ?? '').toString();
  }

  private formatISODateToDMY(raw: any): string {
    if (!raw) return '';

    let y: number, m: number, d: number;
    if (typeof raw === 'string') {
      const [datePart] = raw.split('T');
      const parts = datePart?.split('-');
      if (!parts || parts.length < 3) return raw;
      y = Number(parts[0]); m = Number(parts[1]); d = Number(parts[2]);
    } else if (raw instanceof Date) {
      y = raw.getFullYear(); m = raw.getMonth() + 1; d = raw.getDate();
    } else {
      const tmp = new Date(raw);
      if (isNaN(tmp.getTime())) return (raw ?? '').toString();
      y = tmp.getFullYear(); m = tmp.getMonth() + 1; d = tmp.getDate();
    }

    const dd = String(d).padStart(2, '0');
    const mm = String(m).padStart(2, '0');
    const yyyy = String(y);
    return `${dd}/${mm}/${yyyy}`;
  }
}
